import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const texts = {
  consent: {
    ru: 'Согласие', en: 'Consent', lv: 'Piekrišana', lt: 'Sutikimas', pl: 'Zgoda',
  },
  details: {
    ru: 'Подробности', en: 'Details', lv: 'Detaļas', lt: 'Detalės', pl: 'Szczegóły',
  },
  about: {
    ru: 'О файлах cookie', en: 'About cookies', lv: 'Par sīkdatnēm', lt: 'Apie slapukus', pl: 'O plikach cookie',
  },
  title: {
    ru: 'Этот сайт использует файлы cookie',
    en: 'This website uses cookies',
    lv: 'Šī vietne izmanto sīkdatnes',
    lt: 'Ši svetainė naudoja slapukus',
    pl: 'Ta strona używa plików cookie',
  },
  body: {
    ru: 'Мы используем файлы cookie для улучшения вашего онлайн-опыта. Продолжая использовать наш сайт, мы предполагаем, что вы разрешаете использование этих файлов cookie.',
    en: 'We use cookies to improve your online experience. By continuing to use our website we assume you allow the use of these cookies.',
    lv: 'Mēs izmantojam sīkdatnes, lai uzlabotu jūsu tiešsaistes pieredzi. Turpinot izmantot mūsu vietni, mēs pieņemam, ka jūs atļaujat šo sīkdatņu izmantošanu.',
    lt: 'Mes naudojame slapukus, kad pagerintume jūsų patirtį internete. Toliau naudodami mūsų svetainę, manome, kad leidžiate naudoti šiuos slapukus.',
    pl: 'Używamy plików cookie, aby poprawić Twoje doświadczenia online. Kontynuując korzystanie z naszej strony, zakładamy, że zezwalasz na używanie tych plików cookie.',
  },
  necessary: {
    ru: 'Необходимые', en: 'Necessary', lv: 'Nepieciešamie', lt: 'Būtini', pl: 'Niezbędne',
  },
  necessaryDesc: {
    ru: 'Необходимые файлы cookie помогают улучшить удобство использования сайта, обеспечивая основные функции, такие как навигация по страницам и доступ к защищённым разделам сайта. Сайт не может корректно функционировать без этих файлов cookie.',
    en: 'Necessary cookies help make a website usable by enabling basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.',
    lv: 'Nepieciešamie sīkfaili palīdz padarīt vietni lietojamu, nodrošinot pamata funkcijas, piemēram, lapu navigāciju un piekļuvi drošām vietnes zonām.',
    lt: 'Būtini slapukai padeda padaryti svetainę naudojamą, įgalinant pagrindines funkcijas, tokias kaip puslapių naršymas ir prieiga prie saugių svetainės sričių.',
    pl: 'Niezbędne pliki cookie pomagają uczynić stronę użyteczną, umożliwiając podstawowe funkcje, takie jak nawigacja po stronach i dostęp do bezpiecznych obszarów witryny.',
  },
  preferences: {
    ru: 'Предпочтения', en: 'Preferences', lv: 'Preferences', lt: 'Nuostatos', pl: 'Preferencje',
  },
  statistics: {
    ru: 'Статистика', en: 'Statistics', lv: 'Statistika', lt: 'Statistika', pl: 'Statystyki',
  },
  marketing: {
    ru: 'Маркетинг', en: 'Marketing', lv: 'Mārketings', lt: 'Rinkodara', pl: 'Marketing',
  },
  allowAll: {
    ru: 'Разрешить все', en: 'Allow all', lv: 'Atļaut visus', lt: 'Leisti visus', pl: 'Zezwól na wszystkie',
  },
  allowSelection: {
    ru: 'Разрешить выбранные', en: 'Allow selection', lv: 'Atļaut izvēlētos', lt: 'Leisti pasirinktus', pl: 'Zezwól na wybrane',
  },
  decline: {
    ru: 'Отклонить', en: 'Decline', lv: 'Noraidīt', lt: 'Atmesti', pl: 'Odrzuć',
  },
};

const CookieConsent = () => {
  const { lang } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('consent');
  const [toggles, setToggles] = useState({ preferences: false, statistics: false, marketing: false });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) setVisible(true);
  }, []);

  const handleAccept = (type) => {
    localStorage.setItem('cookie_consent', type);
    setVisible(false);
  };

  const t = (key) => texts[key]?.[lang] || texts[key]?.en || key;

  if (!visible) return null;

  const Toggle = ({ checked, onChange, disabled }) => (
    <button type="button" onClick={disabled ? undefined : onChange}
      className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${
        checked ? 'bg-[#c30909]' : disabled ? 'bg-[#c30909]/60' : 'bg-gray-300'
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <div className={`w-5 h-5 rounded-full bg-white shadow absolute top-0.5 transition-transform ${
        checked || disabled ? 'translate-x-6' : 'translate-x-0.5'
      }`} />
    </button>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-[520px] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header with Cookiebot-style logo */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="w-8 h-8">
            <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8">
              <circle cx="12" cy="12" r="11" stroke="#333" strokeWidth="1.5"/>
              <path d="M7 12l3 3 7-7" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="text-right">
            <p className="text-[11px] text-gray-500 font-medium">Cookiebot</p>
            <p className="text-[9px] text-gray-400">by Usercentrics</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {['consent', 'details', 'about'].map((tab) => (
            <button key={tab} type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-semibold transition-colors relative ${
                activeTab === tab ? 'text-[#c30909]' : 'text-[#484c55]/60 hover:text-[#484c55]'
              }`}>
              {t(tab)}
              {activeTab === tab && <div className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-[#c30909]" />}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {activeTab === 'consent' && (
            <div>
              <h3 className="text-base font-bold text-[#484c55] mb-3">{t('title')}</h3>
              <p className="text-sm text-[#484c55]/70 leading-relaxed">{t('body')}</p>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-4">
              {/* Necessary - always on */}
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#c30909]">{t('necessary')}</span>
                    <span className="text-[10px] bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">9</span>
                  </div>
                  <Toggle checked disabled />
                </div>
                <p className="text-xs text-[#484c55]/60 mt-2 leading-relaxed">{t('necessaryDesc')}</p>
              </div>

              {/* Preferences */}
              <div className="flex items-center justify-between py-2 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#484c55]">{t('preferences')}</span>
                  <span className="text-[10px] bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">3</span>
                </div>
                <Toggle checked={toggles.preferences} onChange={() => setToggles(p => ({ ...p, preferences: !p.preferences }))} />
              </div>

              {/* Statistics */}
              <div className="flex items-center justify-between py-2 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#484c55]">{t('statistics')}</span>
                  <span className="text-[10px] bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">5</span>
                </div>
                <Toggle checked={toggles.statistics} onChange={() => setToggles(p => ({ ...p, statistics: !p.statistics }))} />
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between py-2 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#484c55]">{t('marketing')}</span>
                  <span className="text-[10px] bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">7</span>
                </div>
                <Toggle checked={toggles.marketing} onChange={() => setToggles(p => ({ ...p, marketing: !p.marketing }))} />
              </div>
            </div>
          )}

          {activeTab === 'about' && (
            <div>
              <p className="text-sm text-[#484c55]/70 leading-relaxed">
                {lang === 'ru'
                  ? 'Файлы cookie — это небольшие текстовые файлы, которые могут быть использованы веб-сайтами для повышения эффективности работы пользователя. Закон гласит, что мы можем хранить файлы cookie на вашем устройстве, если они строго необходимы для работы данного сайта. Для всех остальных типов файлов cookie нам необходимо ваше разрешение.'
                  : lang === 'en'
                  ? 'Cookies are small text files that can be used by websites to make a user\'s experience more efficient. The law states that we can store cookies on your device if they are strictly necessary for the operation of this site. For all other types of cookies we need your permission.'
                  : 'Cookies are small text files that can be used by websites to make a user\'s experience more efficient.'
                }
              </p>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="px-5 pb-5 pt-2 space-y-2">
          <button onClick={() => handleAccept('all')}
            className="w-full bg-[#c30909] hover:bg-[#a00707] text-white font-bold text-sm py-3.5 rounded-lg transition-colors">
            {t('allowAll')}
          </button>
          <button onClick={() => handleAccept('selection')}
            className="w-full border-2 border-gray-300 hover:bg-gray-50 text-[#484c55] font-bold text-sm py-3 rounded-lg transition-colors">
            {t('allowSelection')}
          </button>
          <button onClick={() => handleAccept('necessary')}
            className="w-full border-2 border-gray-300 hover:bg-gray-50 text-[#484c55] font-bold text-sm py-3 rounded-lg transition-colors">
            {t('decline')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
